import supabase from '../supabaseClient'
import { TABLE_NAMES } from '../config'
import { stripPlusCodeFromAddress } from './venuesApi'

/** Google Maps URL for a wizard / Places venue record. */
export function mapsUrlFromVenue(venue) {
  if (!venue) return null

  const placeId = venue.placeId
  if (
    placeId &&
    !venue.isManual &&
    !String(placeId).startsWith('manual_')
  ) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.name || '')}&query_place_id=${encodeURIComponent(placeId)}`
  }

  if (venue.lat != null && venue.lng != null) {
    return `https://www.google.com/maps/search/?api=1&query=${venue.lat},${venue.lng}`
  }

  const addr = (venue.formattedAddress || venue.name || '').trim()
  if (addr) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`
  }

  return null
}

function locationKey(name, address) {
  return `${String(name || '').trim().toLowerCase()}|${String(address || '').trim().toLowerCase()}`
}

/**
 * Inserts signup-wizard venues into public.community_locations (same table
 * as Settings → Locations). Skips rows that already match name + address.
 */
export async function persistWizardVenuesToCommunityLocations(
  communityId,
  venues = [],
) {
  if (!communityId || !Array.isArray(venues) || venues.length === 0) {
    return { inserted: 0 }
  }

  const { data: existing, error: fetchError } = await supabase
    .from(TABLE_NAMES.COMMUNITY_LOCATIONS)
    .select('name, address, display_order')
    .eq('community_id', communityId)

  if (fetchError) {
    console.warn(
      'persistWizardVenuesToCommunityLocations: load failed',
      fetchError.message,
    )
    return { inserted: 0, error: fetchError.message }
  }

  const seen = new Set(
    (existing || []).map((row) => locationKey(row.name, row.address)),
  )
  let displayOrder =
    existing?.length > 0
      ? Math.max(...existing.map((r) => r.display_order || 0))
      : 0

  const rows = []
  for (const venue of venues) {
    const name = String(venue?.name || '').trim()
    if (!name) continue

    const address =
      stripPlusCodeFromAddress(venue.formattedAddress || '') || null
    const key = locationKey(name, address)
    if (seen.has(key)) continue
    seen.add(key)

    displayOrder += 1
    rows.push({
      community_id: communityId,
      name,
      address,
      maps_url: mapsUrlFromVenue(venue),
      pitches: [],
      display_order: displayOrder,
      image_url: venue.photoUrl || venue.image_url || null,
    })
  }

  if (rows.length === 0) return { inserted: 0 }

  const { error: insertError } = await supabase
    .from(TABLE_NAMES.COMMUNITY_LOCATIONS)
    .insert(rows)

  if (insertError) {
    console.warn(
      'persistWizardVenuesToCommunityLocations: insert failed',
      insertError.message,
    )
    return { inserted: 0, error: insertError.message }
  }

  return { inserted: rows.length }
}
